const LANGUAGES = ['zh-TW', 'ja', 'en'];

export const DISABLED = Object.freeze({ enabled: false });

export const MAINTENANCE_API_PATH = '/api/maintenance';

/**
 * 開關要在「後端壞掉」的時候還讀得到,所以這條路徑不能經過後端。診所自架的
 * origin(atlas 在服務)把 `/api/*` 全部自己接走,同源的相對路徑到不了 Vercel
 * 的 edge function —— 那裡設 GATSBY_MAINTENANCE_FLAG_URL 指向該環境自己的
 * Vercel host,繞過 atlas 直接問 Global Config。
 *
 * 值是一整條網址,app 內不拼 region / stage 字串(spec §3):拼錯的後果不是
 * 功能失效,是擋錯環境的人。Vercel 自己的 origin 沒設這個變數,走相對路徑。
 */
export const resolveFlagUrl = () =>
  process.env.GATSBY_MAINTENANCE_FLAG_URL || MAINTENANCE_API_PATH;

export const MAINTENANCE_SERVICE_ID = 'clinic-liff';

export const DEFAULT_LOCALE = 'zh-TW';

// 網址上的 locale 參數用的是 tw/jp/en,文案的 key 用的是 BCP 47。兩邊都要
// 認得的地方有三處(Gate、維護頁、fallbackCopy 的查表),所以放這裡共用。
export const LOCALE_MAP = {
  en: 'en',
  jp: 'ja',
  tw: 'zh-TW',
};

export const resolveLocale = (value) => LOCALE_MAP[value] || DEFAULT_LOCALE;

export const resolveEnvTier = () =>
  process.env.VERCEL_TARGET_ENV || process.env.VERCEL_ENV || 'development';

export const resolveEnabledForTier = (enabled, tier) => {
  if (typeof enabled === 'boolean') {
    return enabled;
  }

  if (!enabled || typeof enabled !== 'object') {
    return false;
  }

  return enabled[tier] === true;
};

// 明列欄位,store 以後新增的 key 不會自動出現在這個公開端點。帶 tier 是為了
// 線上能分辨 false 是「沒連 store / 沒注入 env / 那格沒開」哪一種。
export const resolveFlagForTier = (payload, tier = resolveEnvTier()) => ({
  copy: payload.copy,
  enabled: resolveEnabledForTier(payload.enabled, tier),
  services: payload.services,
  tier,
  until: payload.until,
});

export const TAIPEI_OFFSET_MINUTES = 480;
export const TOKYO_OFFSET_MINUTES = 540;

const UNTIL_PATTERN =
  /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:(Z)|([+-])(\d{2}):(\d{2}))?$/;

const isCalendarDate = (year, month, day) => {
  const probe = new Date(Date.UTC(year, month - 1, day));

  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

export const parseUntil = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';

  if (!trimmed) {
    return undefined;
  }

  const match = trimmed.replace(/\//g, '-').match(UNTIL_PATTERN);

  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4] || '0');
  const minute = Number(match[5] || '0');
  const second = Number(match[6] || '0');
  const zulu = match[7];
  const offsetSign = match[8];

  if (
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    !isCalendarDate(year, month, day)
  ) {
    return undefined;
  }

  if (zulu || offsetSign) {
    const offsetMinutes = zulu
      ? 0
      : (offsetSign === '+' ? 1 : -1) *
        (Number(match[9]) * 60 + Number(match[10]));
    const utc = Date.UTC(year, month - 1, day, hour, minute, second);

    return new Date(utc - offsetMinutes * 60 * 1000);
  }

  // 無 offset 一律當 Asia/Taipei(UTC+8)牆上時間,不隨訪客裝置時區變動
  return new Date(
    Date.UTC(year, month - 1, day, hour, minute, second) -
      TAIPEI_OFFSET_MINUTES * 60 * 1000
  );
};

const parseCopy = (copy) => {
  if (!copy || typeof copy !== 'object') {
    return undefined;
  }

  const parsed = LANGUAGES.reduce((acc, language) => {
    const entry = copy[language];
    const title = typeof entry?.title === 'string' ? entry.title.trim() : '';
    const description =
      typeof entry?.description === 'string' ? entry.description.trim() : '';

    if (title || description) {
      acc[language] = { description, title };
    }

    return acc;
  }, {});

  return Object.keys(parsed).length > 0 ? parsed : undefined;
};

const includesThisService = (services) => {
  if (!Array.isArray(services) || services.length === 0) {
    return true;
  }

  return services.includes(MAINTENANCE_SERVICE_ID);
};

export const normalizeMaintenanceConfig = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return DISABLED;
  }

  const { copy, enabled, services, until } = payload;

  if (
    !resolveEnabledForTier(enabled, resolveEnvTier()) ||
    !includesThisService(services)
  ) {
    return DISABLED;
  }

  return {
    copy: parseCopy(copy),
    enabled: true,
    until: parseUntil(typeof until === 'string' ? until : undefined),
  };
};
